import { FC, HTMLAttributes, useEffect, useState } from 'react';
import clx from 'classnames';
import { EditorState, convertToRaw, ContentState } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';

import Button from '../Button';
import { useToggle } from 'src/Hooks';

import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { ReactComponent as CloseIcon } from 'src/Themes/assets/close small.svg';
import { ReactComponent as TickIcon } from 'src/Themes/assets/tick small.svg';
import './ViewEditor.scss';

interface IViewEditorProps extends HTMLAttributes<HTMLDivElement> {
	readonly?: boolean;
	value?: string;
	placeholder?: string;

	onSaveContent?: (value: string) => void;
	onReset?: () => void;
}

const ViewEditor: FC<IViewEditorProps> = (props) => {
	const {
		value = '',
		onSaveContent,
		onReset,
		placeholder,
		readonly = false,
		className,
		...otherProps
	} = props;

	const [needReload, reload] = useToggle(false);
	const [state, setState] = useState<EditorState>(EditorState.createEmpty());

	useEffect(() => {
		if (readonly) {
			return;
		}
		const contentBlock = htmlToDraft(value || '');
		if (contentBlock) {
			const contentState = ContentState.createFromBlockArray(
				contentBlock.contentBlocks,
			);
			const editorState = EditorState.createWithContent(contentState);
			setState(editorState);
		}
		// eslint-disable-next-line
	}, [value, needReload]);

	const onSaveContentHandler = () => {
		if (!readonly && !!onSaveContent) {
			onSaveContent(draftToHtml(convertToRaw(state.getCurrentContent())));
		}
	};

	const onResetHandler = () => {
		reload();
		if (onReset) {
			onReset();
		}
	};

	return (
		<div className={clx('main-wrapper', className)} {...otherProps}>
			<Editor
				editorState={state}
				placeholder={placeholder}
				onEditorStateChange={setState}
				toolbar={editorOptions}
				// toolbarCustomButtons={[ renderSaveIcon() ]}
				hashtag={{
					separator: ' ',
					trigger: '#',
				}}
				localization={{ locale: 'ru' }}
			/>
			<div className="toolbar">
				<Button
					outline
					size="min"
					onClick={onSaveContentHandler}
					disable={!state.getUndoStack().size}
				>
					<TickIcon />
				</Button>
				<Button outline size="min" onClick={onResetHandler}>
					<CloseIcon />
				</Button>
			</div>
		</div>
	);
};

const editorOptions = {
	options: ['inline', 'fontSize', 'list', 'textAlign', 'link', 'history'],
	inline: {
		inDropdown: false,
		options: ['bold', 'italic', 'underline', 'strikethrough'],
	},
	fontSize: {
		options: [14, 16, 18, 24, 30, 36],
		className: undefined,
		component: undefined,
		dropdownClassName: undefined,
	},
	textAlign: {
		inDropdown: true,
	},
};

export default ViewEditor;
